import React, { useEffect, useRef, useState } from "react";
import StatCardsParent from "app/patch/components/StatCards/StatCardsParent";
import HorizontalTabs from "common-ui-components/components/horizontal-tabs/HorizontalTabs";
import styles from "./VulnerabilitiesDashboard.module.css";
import { ResponsiveBar } from "@nivo/bar";
import stackedBarGraphSvg from "common-ui-components/components/icons/stacked-bar-graph.svg";
import groupedBarGraphSvg from "common-ui-components/components/icons/grouped-bar-graph.svg";
import CVEListTable from "./components/CVEListTable";
import { toTitleCase } from "common-ui-components/components/utils/string-utils";
import Select from "common-ui-components/components/select";
import VulnerableSoftwareList from "./components/VulnerableSoftwareList";
import SeverityBreakdownChart from "./components/SeverityBreakdownChart";
import API from "common-ui-components/components/utils/API";
import { useLocation } from "react-router-dom";
import refreshIcon from "common-ui-components/components/icons/refresh.svg";
import tickIcon from "app/TickMark.svg";
import { notify } from "common-ui-components/components/notification/Notification";
import {
  Success,
  Failure,
} from "common-ui-components/components/notification/templates";

const TREND_OPTIONS = ["Daily", "Weekly", "Monthly"];

const TREND_TYPE_MAP = {
  Daily: "DAILY",
  Weekly: "WEEKLY",
  Monthly: "MONTHLY",
};

const TREND_LEGEND_MAP = {
  Daily: "Scan Period (Daily)",
  Weekly: "Scan Period (Weekly)",
  Monthly: "Scan Period (Monthly)",
};

const CHART_MODES = [
  { key: "stacked", label: "Stacked View", icon: stackedBarGraphSvg },
  { key: "grouped", label: "Grouped View", icon: groupedBarGraphSvg },
];

const getPeriodLabel = (item, trend) => {
  if (trend === "Monthly") {
    return toTitleCase(item.monthName ?? "");
  }
  if (trend === "Weekly") {
    return `Week ${item.week}`;
  }
  return item.date ? item.date.split(" ")[0] : "";
};

const getPeriodRange = (item, trend) => {
  if (trend === "Weekly" && item.weekStartDate && item.weekEndDate) {
    return `${item.weekStartDate} - ${item.weekEndDate}`;
  }
  return null;
};

export const VulnerabilityBarChart = ({
  onRefresh,
  refreshStatus = "idle",
  showModeSwitch = true,
  containerStyle,
}) => {
  const [chartModeSwitchImg, setChartModeSwitchImg] = useState("stacked");
  const chartMode = showModeSwitch ? chartModeSwitchImg : "stacked";
  const [selectedTrendIndex, setSelectedTrendIndex] = useState(
    TREND_OPTIONS.indexOf("Weekly"),
  );
  const selectedTrend = TREND_OPTIONS[selectedTrendIndex];
  const [vulnerabilityData, setVulnerabilityData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchTrends = async () => {
      setLoading(true);
      try {
        const response = await API.get(
          `vulnerabilities/getTrends?trendType=${TREND_TYPE_MAP[selectedTrend]}`,
        );
        if (isMounted) {
          if (response?.status?.success) {
            const mapped = (response.body ?? []).map((item) => ({
              period: getPeriodLabel(item, selectedTrend),
              fullRange: getPeriodRange(item, selectedTrend),
              Critical: item.criticalVulnerabilityCount ?? 0,
              High: item.importantVulnerabilityCount ?? 0,
              Medium: item.moderateVulnerabilityCount ?? 0,
              Low: item.lowVulnerabilityCount ?? 0,
            }));
            setVulnerabilityData(mapped);
          } else {
            setVulnerabilityData([]);
          }
        }
      } catch (error) {
        if (isMounted) {
          setVulnerabilityData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTrends();

    return () => {
      isMounted = false;
    };
  }, [selectedTrend]);

  const CustomTick = ({ x, y, value, opacity, textAnchor, textBaseline }) => {
    const item = vulnerabilityData.find((d) => d.period === value);
    const tooltip = item?.fullRange;
    return (
      <g transform={`translate(${x},${y})`} style={{ opacity }}>
        <line stroke="#bbb" strokeWidth={1} y1={0} y2={5} />
        <text
          transform="rotate(-45)"
          dominantBaseline={textBaseline}
          textAnchor={textAnchor}
          y={9}
          style={{ fontSize: 11, fill: "#333333" }}
        >
          {value}
          {tooltip && <title>{tooltip}</title>}
        </text>
      </g>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        width: "100%",
        background: "#ffffff",
        padding: "24px",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        ...containerStyle,
      }}
    >
      <div
        style={{
          marginBottom: "16px",
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "4px",
            }}
          >
            <h2 style={{ margin: 0, fontSize: "1.2rem", color: "#333" }}>
              Security Vulnerability Trends
            </h2>
            {onRefresh && (
              <button
                type="button"
                className={styles.refreshButton}
                onClick={onRefresh}
                disabled={refreshStatus === "refreshing"}
                aria-label="Refresh vulnerabilities"
                title="Refresh"
              >
                <img
                  height={20}
                  src={refreshStatus === "complete" ? tickIcon : refreshIcon}
                  alt=""
                  className={
                    refreshStatus === "refreshing" ? styles.rotate : ""
                  }
                />
              </button>
            )}
          </div>
          <p style={{ margin: 0, fontSize: "0.9rem", color: "#666" }}>
            Breakdown of detected vulnerabilities by severity level over time.
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          {showModeSwitch && (
            <div
              style={{
                display: "flex",
                border: "1px solid #d0d5dd",
                borderRadius: "6px",
                overflow: "hidden",
                height: "2.28rem",
                background: "#f5f6f8",
                padding: "2px",
                gap: "2px",
              }}
            >
              {CHART_MODES.map(({ key, label, icon }) => {
                const isActive = chartModeSwitchImg === key;
                return (
                  <button
                    key={key}
                    type="button"
                    title={label}
                    onClick={() => setChartModeSwitchImg(key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "100%",
                      border: "none",
                      borderRadius: "4px",
                      background: isActive ? "#ffffff" : "transparent",
                      boxShadow: isActive
                        ? "0 1px 2px rgba(16, 24, 40, 0.08), 0 0 0 1px rgba(49, 116, 160, 0.25)"
                        : "none",
                      cursor: "pointer",
                      padding: 0,
                      transition:
                        "background-color 0.15s ease, box-shadow 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive)
                        e.currentTarget.style.background = "#e9ebef";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive)
                        e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <img
                      src={icon}
                      width={16}
                      height={16}
                      alt={label}
                      style={{
                        opacity: isActive ? 1 : 0.55,
                        filter: isActive
                          ? "invert(29%) sepia(23%) saturate(1284%) hue-rotate(163deg) brightness(94%) contrast(88%)"
                          : "none",
                        transition: "opacity 0.15s ease",
                      }}
                    />
                  </button>
                );
              })}
            </div>
          )}
          <Select
            options={TREND_OPTIONS}
            active={selectedTrendIndex}
            onOptionSelect={(_option, index) => setSelectedTrendIndex(index)}
            triggerProps={{
              className: styles.dropdown,
            }}
            iconProps={{
              style: {
                marginTop: "-0.3rem",
              },
            }}
          />
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                color: "#666",
                fontSize: "0.9rem",
              }}
            >
              Loading trends...
            </div>
          ) : vulnerabilityData.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                color: "#666",
                fontSize: "0.9rem",
              }}
            >
              No vulnerability trend data available.
            </div>
          ) : (
            <ResponsiveBar
              data={vulnerabilityData}
              keys={["Low", "Medium", "High", "Critical"]}
              indexBy="period"
              groupMode={chartMode}
              margin={{ top: 20, right: 130, bottom: 100, left: 60 }}
              padding={chartMode === "stacked" ? 0.6 : 0.3}
              valueScale={{ type: "linear" }}
              indexScale={{ type: "band", round: true }}
              colors={({ id }) => {
                if (id === "Critical") return "#e0432a";
                if (id === "High") return "#c026b8";
                if (id === "Medium") return "#2f6fed";
                return "#0e9384";
              }}
              enableGridY={true}
              enableGridX={false}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 8,
                tickRotation: -45,
                legend: TREND_LEGEND_MAP[selectedTrend],
                legendPosition: "middle",
                legendOffset: 80,
                renderTick: CustomTick,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "Total Vulnerabilities Detected",
                legendPosition: "middle",
                legendOffset: -45,
              }}
              enableLabel={true}
              labelSkipWidth={10}
              labelSkipHeight={10}
              labelTextColor="#ffffff"
              isInteractive={true}
              theme={{
                tooltip: {
                  container: {
                    background: "transparent",
                    padding: 0,
                    boxShadow: "none",
                    borderRadius: 0,
                  },
                },
                axis: {
                  legend: {
                    text: {
                      fontWeight: "bold",
                      fill: "#333333",
                    },
                  },
                },
              }}
              tooltip={({ id, value, color, indexValue }) => (
                <div
                  style={{
                    padding: "4px 8px",
                    background: "#222",
                    color: "#fff",
                    fontSize: "11px",
                    borderRadius: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "8px",
                      height: "8px",
                      backgroundColor: color,
                      borderRadius: "50%",
                    }}
                  />
                  <strong>{indexValue}</strong>: {value} {id}
                </div>
              )}
              legends={[
                {
                  dataFrom: "keys",
                  anchor: "bottom-right",
                  direction: "column",
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 4,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: "left-to-right",
                  itemOpacity: 0.85,
                  symbolSize: 15,
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
              animate={true}
              motionConfig="gentle"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const VulnerabilitiesDashboard = () => {
  const location = useLocation();
  const [vulnerabilitiesCount, setVulnerabilitiesCount] = useState(null);
  const [cveFilter, setCveFilter] = useState(() => {
    const stateFilters = location.state?.active_filters;
    const stateKey = location.state?.activeFiltersKey;
    if (stateFilters && Object.keys(stateFilters).length > 0) {
      return { activeFilters: stateFilters, key: stateKey || 1 };
    }
    return { activeFilters: {}, key: 0 };
  });
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [refreshStatus, setRefreshStatus] = useState("idle");
  const [refreshKey, setRefreshKey] = useState(0);
  const cveSectionRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const [riskIndicators, setRiskIndicators] = useState({
    activelyExploitedCount: 0,
    cisaKEVCount: 0,
    zeroDayCount: 0,
    pastDueCount: 0,
  });

  // Clear the "complete -> idle" timer if the page unmounts
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchRiskIndicators = async () => {
      try {
        const response = await API.get("vulnerabilities/risk-indicators");
        if (isMounted && response?.status?.success) {
          setRiskIndicators(response.body);
        }
      } catch (error) {
        console.error("Error fetching risk indicators:", error);
      }
    };

    fetchRiskIndicators();

    return () => {
      isMounted = false;
    };
  }, [refreshKey]);

  const handleRefresh = async () => {
    setRefreshStatus("refreshing");
    try {
      const response = await API.get("inventory/UpdateSummary");
      if (response?.status?.success) {
        notify(<Success>Summary Updated</Success>);
        setRefreshKey((current) => current + 1);
      } else {
        notify(<Failure>Refresh Failed</Failure>);
      }
    } catch (error) {
      console.error("API call failed:", error);
      notify(<Failure>Refresh Failed</Failure>);
    } finally {
      setRefreshStatus("complete");
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(
        () => setRefreshStatus("idle"),
        4000,
      );
    }
  };

  const statCardsData = [
    {
      label: "Actively exploited",
      value: riskIndicators.activelyExploitedCount,
      color: "#ef4444",
      filter: { column: "exploitationStatus", value: ["Active"] },
    },
    {
      label: "CISA KEV listed",
      value: riskIndicators.cisaKEVCount,
      color: "#a855f7",
      filter: { column: "cisaKEV", value: ["true"] },
    },
    {
      label: "Zero-day",
      value: riskIndicators.zeroDayCount,
      color: "#6366f1",
      filter: { column: "zeroDay", value: ["true"] },
    },
    {
      label: "Past due remediation",
      value: riskIndicators.pastDueCount,
      color: "#374151",
    },
  ];

  const handleCveFilterClick = (filter) => {
    setActiveTabIndex(0);
    setCveFilter((current) => {
      const isActive = current.activeFilters?.comprehensive_filters?.some(
        (activeFilter) =>
          activeFilter.column === filter.column &&
          JSON.stringify(activeFilter.value) === JSON.stringify(filter.value),
      );

      return {
        activeFilters: isActive ? {} : { comprehensive_filters: [filter] },
        key: current.key + 1,
      };
    });
  };

  const handleSeveritySelect = (severity) => {
    handleCveFilterClick({ column: "severity", value: [severity] });
  };

  useEffect(() => {
    if (cveFilter.key === 0) return;

    cveSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [cveFilter.key]);

  const cardsWithFilters = statCardsData.map(({ filter, ...card }) => ({
    ...card,
    ...(filter && { onClick: () => handleCveFilterClick(filter) }),
  }));

  return (
    <div className={styles.container}>
      <div className={styles.visualsSection}>
        <div className={styles.headerFlex}>
          <div className={styles.header}>
            <div className={styles.titleRow}>
              <b className={styles.title}>Vulnerabilities</b>
            </div>
            <p className={styles.subtitle}>
              <span className={[styles.highlight, "red"].join(" ")}>
                {vulnerabilitiesCount ?? "..."} vulnerabilities
              </span>{" "}
              detected across managed endpoints.
            </p>
          </div>
        </div>

        <div className={styles.grid}>
          <SeverityBreakdownChart
            key={`severity-${refreshKey}`}
            setVulnerabilitiesCount={setVulnerabilitiesCount}
            onSeveritySelect={handleSeveritySelect}
          />

          <StatCardsParent
            key={`risk-${refreshKey}`}
            title="Risk Indicators"
            dataItems={cardsWithFilters}
          />
        </div>

        <VulnerabilityBarChart
          key={`trend-${refreshKey}`}
          onRefresh={handleRefresh}
          refreshStatus={refreshStatus}
        />
      </div>

      <HorizontalTabs
        ref={cveSectionRef}
        key={`tabs-${refreshKey}`}
        initialTabIndex={activeTabIndex}
        onTabChange={setActiveTabIndex}
        containerProps={{
          className: styles.tabs,
          style: { scrollMarginTop: "var(--horizontal-bar-height, 56px)" },
        }}
        tabs={[
          {
            header: "CVE View",
            children: <CVEListTable cveFilter={cveFilter} />,
          },
          {
            header: "Software View",
            children: <VulnerableSoftwareList />,
          },
        ]}
      />
    </div>
  );
};

export default VulnerabilitiesDashboard;
